// /crm/deals/new — cadastro de deal (Story 11.4)
// Reusa DealForm com pickers de responsável (useUsers), produto SaaS e plano.

import { ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DealForm } from '../../../components/crm/DealForm';
import { useCreateCrmDeal } from '../../../hooks/use-crm-deals';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { usePlans } from '../../../hooks/use-plans';
import { useUsers } from '../../../hooks/use-users';
import { extractErrorMessage } from '../../../lib/errors';

export function NewCrmDealPage(): JSX.Element {
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();
  const create = useCreateCrmDeal();
  const { data: saasProducts } = useSaasProducts({ limit: 100 });
  const { data: plans } = usePlans({ limit: 100 });
  const { data: responsibleUsers } = useUsers();

  // RBAC (FR-59.2 / AC3): read-only roles não acessam o fluxo de criação.
  if (!canWrite) {
    return <Navigate to="/crm/deals" replace />;
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 pb-10">
      <header className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate('/crm/deals')}
          className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Deals
        </button>
        <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] text-ink">
          Novo deal
        </h1>
        <p className="text-[13px] text-muted">
          Abra uma negociação no funil. Só o título é obrigatório — o resto você complementa depois.
        </p>
      </header>

      <DealForm
        isPending={create.isPending}
        submitLabel="Abrir deal"
        responsibleUsers={responsibleUsers ?? []}
        saasProducts={saasProducts?.items ?? []}
        plans={plans?.items ?? []}
        onCancel={() => navigate('/crm/deals')}
        onSubmit={async (values) => {
          try {
            const deal = await create.mutateAsync(values);
            toast.success('Deal aberto.', { description: deal.title });
            navigate('/crm/deals');
          } catch (err) {
            toast.error('Não conseguimos abrir o deal.', {
              description: extractErrorMessage(err, 'Tente de novo em instantes.'),
              action: {
                label: 'Tentar de novo',
                onClick: () => void create.mutateAsync(values),
              },
            });
          }
        }}
      />
    </div>
  );
}
